import { Check, ChevronsUpDown } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

import { usePartnerScope } from "./partner-scope-context";

/**
 * Dropdown in the topbar that lets the admin flip between partners they
 * have a role on. Closes on outside-click and Escape. Keyboard nav:
 * ArrowUp/Down moves highlight, Enter commits, Escape closes.
 *
 * ARIA: the menu uses `role="menu"` + `role="menuitem"`. The earlier
 * listbox-with-focusable-options pattern is invalid (a listbox's
 * children shouldn't be interactive); menu/menuitem cleanly expresses
 * "a list of actions, keyboard-driven from the trigger button."
 *
 * Global listeners (mousedown / keydown) are registered once per open
 * transition. Cursor + available are read through refs so rapid
 * arrow-key movement doesn't re-register the handlers on every frame.
 */
export function PartnerSwitcher(): JSX.Element {
  const { scope, available, setScope } = usePartnerScope();
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Refs shadow the latest cursor + available + setScope so the
  // global-listener effect can depend only on `open`.
  const cursorRef = useRef(cursor);
  const availableRef = useRef(available);
  const setScopeRef = useRef(setScope);
  cursorRef.current = cursor;
  availableRef.current = available;
  setScopeRef.current = setScope;

  const close = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        close();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setCursor((c) => Math.min(availableRef.current.length - 1, c + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setCursor((c) => Math.max(0, c - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const target = availableRef.current[cursorRef.current];
        if (target) {
          setScopeRef.current(target);
          setOpen(false);
        }
      }
    };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  useEffect(() => {
    if (open) {
      const idx = available.findIndex((t) => t.id === scope.id);
      setCursor(idx === -1 ? 0 : idx);
    }
  }, [open, available, scope.id]);

  // No menu needed when there's nothing to switch to.
  const interactive = available.length > 1;

  return (
    <div ref={rootRef} className="relative min-w-0 flex-shrink">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={!interactive}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-md border bg-card px-3 py-1.5 text-sm",
          // Mobile: shrink freely; Desktop: give it breathing room.
          "max-w-[180px] md:min-w-[240px] md:max-w-[280px]",
          interactive ? "hover:bg-muted" : "cursor-default opacity-80",
        )}
      >
        <span className="flex min-w-0 flex-col items-start leading-tight">
          {/* "Scope" label wastes vertical space on narrow topbars - show on md+. */}
          <span className="hidden text-xs text-muted-foreground md:block">Scope</span>
          <span className="w-full truncate text-left font-medium">{scope.name}</span>
        </span>
        {interactive && (
          <ChevronsUpDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Partner scope"
          className="absolute right-0 z-20 mt-1 max-h-80 w-80 overflow-auto rounded-md border bg-card py-1 text-sm shadow-lg"
        >
          {available.map((t, i) => {
            const active = t.id === scope.id;
            const highlighted = i === cursor;
            return (
              <button
                key={t.id}
                type="button"
                role="menuitem"
                aria-current={active ? "true" : undefined}
                onMouseEnter={() => setCursor(i)}
                onClick={() => {
                  setScope(t);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center justify-between gap-3 px-3 py-2 text-left",
                  highlighted && "bg-muted",
                )}
              >
                <span className="truncate font-medium">{t.name}</span>
                {active && <Check className="h-4 w-4 text-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
